import React, { useState, useEffect } from 'react';
import { 
  Box,  
  Paper, 
  Typography, 
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Avatar,
  Chip,
  ListItemIcon,
  Fade,
  Skeleton
} from '@mui/material';
import Grid from '@mui/material/Grid'
import { 
  ViewList as TableIcon, 
  SwapHoriz as TransferIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Create as CreateIcon,
  Update as UpdateIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PageHeader from '../components/common/PageHeader';
import { selectUser } from '../store/slices/authSlice';
import { TableService } from '../services/table.service';
import { TransferService } from '../services/transfer.service';
import { AuditService } from '../services/audit.service';
import { DataTable } from '../types/table.types';
import { DataTransfer, TransferStatus } from '../types/transfer.types';
import { AuditLog } from '../types/audit.types';
import dayjs from 'dayjs';

// Компонент статистической карточки
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  trend?: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, gradient, trend, loading }) => {
  return (
    <Card
      sx={{
        background: gradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          transform: 'translate(30%, -30%)',
        }
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
          {trend && (
            <Chip 
              label={trend} 
              size="small"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600
              }}
            />
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {loading ? <Skeleton width={80} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} /> : value}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Получение иконки для действия
const getActionIcon = (action: string) => {
  switch (action) {
    case 'create': return <CreateIcon fontSize="small" />;
    case 'update': return <UpdateIcon fontSize="small" />;
    case 'delete': return <DeleteIcon fontSize="small" />;
    case 'read': return <VisibilityIcon fontSize="small" />;
    case 'login': return <LoginIcon fontSize="small" />;
    case 'logout': return <LogoutIcon fontSize="small" />;
    case 'transfer': return <SendIcon fontSize="small" />;
    default: return <HistoryIcon fontSize="small" />;
  }
};

// Получение цвета для действия
const getActionColor = (action: string) => {
  switch (action) {
    case 'create': return '#4caf50';
    case 'update': return '#2196f3';
    case 'delete': return '#f44336';
    case 'read': return '#9c27b0';
    case 'login': return '#00bcd4';
    case 'logout': return '#ff9800';
    case 'transfer': return '#673ab7';
    default: return '#757575';
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  
  const [recentTables, setRecentTables] = useState<DataTable[]>([]);
  const [pendingTransfers, setPendingTransfers] = useState<DataTransfer[]>([]);
  const [recentActivity, setRecentActivity] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState({
    tables: true,
    transfers: true,
    activity: true
  });
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      // Получаем недавние таблицы
      setLoading(prev => ({ ...prev, tables: true }));
      const tables = await TableService.getAllTables();
      setRecentTables(tables.slice(0, 5));
      
      // Получаем ожидающие передачи
      setLoading(prev => ({ ...prev, transfers: true }));
      let transfers;
      if (user?.roles.some(role => role.name === 'ADMIN')) {
        transfers = await TransferService.getAllTransfers();
      } else {
        transfers = await TransferService.getReceivedTransfers();
      }
      setPendingTransfers(transfers.filter(t => t.status === TransferStatus.PENDING).slice(0, 5));
      
      // Получаем недавнюю активность пользователя
      setLoading(prev => ({ ...prev, activity: true }));
      const activity = await AuditService.getCurrentUserLogs({ page: 1, pageSize: 10 });
      setRecentActivity(activity.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading({
        tables: false,
        transfers: false,
        activity: false
      });
    }
  };
  
  return (
    <Box>
      {/* Заголовок с градиентом */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 4,
          mb: 4,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          }
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, position: 'relative', zIndex: 1 }}>
          Добро пожаловать, {user?.fullName || 'Пользователь'}! 👋
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, position: 'relative', zIndex: 1 }}>
          Обзор вашей активности и доступных данных
        </Typography>
      </Box>

      {/* Статистические карточки */}
      <Fade in={mounted} timeout={600}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              title="Доступные таблицы"
              value={loading.tables ? '...' : recentTables.length}
              icon={<TableIcon />}
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              loading={loading.tables}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              title="Ожидающие передачи"
              value={loading.transfers ? '...' : pendingTransfers.length}
              icon={<TransferIcon />}
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              loading={loading.transfers}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              title="Активность сегодня"
              value={loading.activity ? '...' : recentActivity.length}
              icon={<TrendingUpIcon />}
              gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              loading={loading.activity}
            />
          </Grid>
        </Grid>
      </Fade>
      
      <Grid container spacing={3}>
        {/* Недавние таблицы */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Fade in={mounted} timeout={800}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  p: 2.5,
                  color: 'white'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TableIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Недавние таблицы
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ p: 0 }}>
                {loading.tables ? (
                  <Box sx={{ p: 3 }}>
                    {[1, 2, 3].map((i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Skeleton height={60} />
                      </Box>
                    ))}
                  </Box>
                ) : recentTables.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <TableIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      У вас еще нет доступных таблиц
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {recentTables.map((table, index) => (
                      <React.Fragment key={table.id}>
                        <ListItemButton
                          onClick={() => navigate(`/tables/${table.id}`)}
                          sx={{
                            py: 2,
                            px: 3,
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'rgba(102, 126, 234, 0.08)',
                              pl: 4,
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                color: '#667eea',
                                width: 40,
                                height: 40
                              }}
                            >
                              <AssessmentIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {table.name}
                              </Typography>
                            }
                            secondary={table.description || `${table.columns.length} колонок`}
                          />
                          <ArrowForwardIcon sx={{ color: 'action.active', opacity: 0.5 }} />
                        </ListItemButton>
                        {index < recentTables.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
              
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button 
                  fullWidth
                  variant="outlined"
                  startIcon={<TableIcon />}
                  onClick={() => navigate('/tables')}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#667eea',
                      bgcolor: 'rgba(102, 126, 234, 0.08)',
                    }
                  }}
                >
                  Все таблицы
                </Button>
              </CardActions>
            </Card>
          </Fade>
        </Grid>
        
        {/* Ожидающие передачи */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Fade in={mounted} timeout={1000}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  p: 2.5,
                  color: 'white'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TransferIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Ожидающие передачи
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ p: 0 }}>
                {loading.transfers ? (
                  <Box sx={{ p: 3 }}>
                    {[1, 2, 3].map((i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Skeleton height={60} />
                      </Box>
                    ))}
                  </Box>
                ) : pendingTransfers.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <TransferIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Нет ожидающих передач
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {pendingTransfers.map((transfer, index) => (
                      <React.Fragment key={transfer.id}>
                        <ListItemButton
                          onClick={() => navigate(`/transfers/${transfer.id}`)}
                          sx={{
                            py: 2,
                            px: 3,
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'rgba(245, 87, 108, 0.08)',
                              pl: 4,
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: 'rgba(245, 87, 108, 0.1)',
                                color: '#f5576c',
                                width: 40,
                                height: 40
                              }}
                            >
                              <PeopleIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                От: {transfer.sourceUser?.fullName}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip 
                                  label={`${transfer.items?.length || 0} элементов`}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.75rem' }}
                                />
                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                                  {dayjs(transfer.createdAt).format('DD.MM.YYYY')}
                                </Typography>
                              </Box>
                            }
                          />
                          <ArrowForwardIcon sx={{ color: 'action.active', opacity: 0.5 }} />
                        </ListItemButton>
                        {index < pendingTransfers.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
              
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button 
                  fullWidth
                  variant="outlined"
                  startIcon={<TransferIcon />}
                  onClick={() => navigate('/transfers')}
                  sx={{
                    borderColor: '#f5576c',
                    color: '#f5576c',
                    '&:hover': {
                      borderColor: '#f5576c',
                      bgcolor: 'rgba(245, 87, 108, 0.08)',
                    }
                  }}
                >
                  Все передачи
                </Button>
              </CardActions>
            </Card>
          </Fade>
        </Grid>
        
        {/* Недавняя активность */}
        <Grid size={{ xs: 12 }}>
          <Fade in={mounted} timeout={1200}>
            <Card
              sx={{
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  p: 2.5,
                  color: 'white'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <HistoryIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Ваша недавняя активность
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ p: 0 }}>
                {loading.activity ? (
                  <Box sx={{ p: 3 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Skeleton height={60} />
                      </Box>
                    ))}
                  </Box>
                ) : recentActivity.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Нет недавней активности
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {recentActivity.map((log, index) => (
                      <React.Fragment key={log.id}>
                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'rgba(79, 172, 254, 0.05)',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: getActionColor(log.action),
                                width: 40,
                                height: 40,
                                color: 'white'
                              }}
                            >
                              {getActionIcon(log.action)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {getActivityText(log)}
                              </Typography>
                            }
                            secondary={
                              <Typography 
                                variant="caption" 
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                              >
                                <AccessTimeIcon sx={{ fontSize: 14 }} />
                                {dayjs(log.createdAt).format('DD.MM.YYYY HH:mm')}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < recentActivity.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
              
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button 
                  fullWidth
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/audit/user')}
                  sx={{
                    borderColor: '#4facfe',
                    color: '#4facfe',
                    '&:hover': {
                      borderColor: '#4facfe',
                      bgcolor: 'rgba(79, 172, 254, 0.08)',
                    }
                  }}
                >
                  Вся история
                </Button>
              </CardActions>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
}

// Функция для формирования текста активности
function getActivityText(log: AuditLog): string {
  switch (log.action) {
    case 'create':
      return `Создано: ${getEntityName(log.entityType)} (${log.entityId?.substring(0, 8) || ''})`;
    case 'update':
      return `Обновлено: ${getEntityName(log.entityType)} (${log.entityId?.substring(0, 8) || ''})`;
    case 'delete':
      return `Удалено: ${getEntityName(log.entityType)} (${log.entityId?.substring(0, 8) || ''})`;
    case 'read':
      return `Просмотрено: ${getEntityName(log.entityType)} (${log.entityId?.substring(0, 8) || ''})`;
    case 'login':
      return 'Выполнен вход в систему';
    case 'logout':
      return 'Выполнен выход из системы';
    case 'transfer':
      return `Передача данных: ${log.entityId?.substring(0, 8) || ''}`;
    default:
      return `Действие: ${log.action}`;
  }
}

function getEntityName(entityType?: string): string {
  if (!entityType) return 'неизвестный объект';
  
  switch (entityType) {
    case 'user':
      return 'пользователь';
    case 'table':
      return 'таблица';
    case 'table_column':
      return 'колонка таблицы';
    case 'table_data':
      return 'данные таблицы';
    case 'table_permission':
      return 'разрешение таблицы';
    case 'data_transfer':
      return 'передача данных';
    default:
      return entityType;
  }
}